import providerService from '../../services/providerProfiles';
import patientService from '../../services/patientProfiles';
import appointmentService from '../../services/appointment';
import numberUtil from '../../utils/number';
import moment from 'moment-timezone';
import provider from '../../services/providerProfiles';

export default {
  data() {
    return {
      providerId: null,
      providerOptions: [],
      providerFieldMessage: '',
      isProviderIdValid: false,
      patientSupportPin: null,
      patientFieldMessage: '',
      isPatientProfileIdValid: false,
      patientProfileId: null,
      durations: [
        {
          text: '60 minutes',
          value: 60,
        },
        {
          text: '30 minutes',
          value: 30,
        },
      ],
      duration: null,
      appointmentDate: null,
      minDate: `${new Date().getFullYear()}-${(new Date().getMonth() + 1).toString().padStart(2, '0')}-${new Date()
        .getDate()
        .toString()
        .padStart(2, '0')}`,
      cachedAvailableDateSlot: {},
      cachedAvailableDateSlotMonthLoaded: {},
      appointmentTimes: [],
      appointmentTime: null,
      priceDetail: null,
      isShowSnackbar: false,
      snackbarText: '',
      organizations: [],
      selectedOrganizationId: 9999, // default to B2C
      organizationFieldMessage: 'B2C',
    };
  },
  watch: {
    providerId() {
      this.providerFieldMessage = '';
      this.checkPrice();
    },
    patientSupportPin() {
      this.patientProfileId = null;
      this.patientFieldMessage = '';
      this.checkPrice();
    },
    duration() {
      this.appointmentDate = null;
      this.appointmentTime = null;
      this.checkPrice();
    },
    appointmentDate() {
      this.appointmentTime = null;
      this.checkPrice();
    },
    appointmentTime() {
      this.checkPrice();
    },
    selectedOrganizationId() {
      this.providerId = null;
      this.resetAvailableDateSlot();
      this.checkPrice();
      this.fetchProviderOption(this.selectedOrganizationId);
      this.organizationFieldMessage =
        this.organizations.find((org) => org.id === this.selectedOrganizationId).name || 'B2C';
    },
  },
  computed: {
    isDurationValid() {
      return this.duration !== null;
    },
    utcAppointmentDateTime() {
      const localDateTime = new Date(`${this.appointmentDate} ${this.appointmentTime}`);
      return moment(localDateTime).utc().format('yyyy-MM-DD HH:mm:ss');
    },
    isValid() {
      let valid = true;

      if (!this.isProviderIdValid) return false;
      if (!this.isPatientProfileIdValid) return false;
      if (this.duration === null) return false;
      if (this.appointmentDate === null) return false;
      if (this.appointmentTime === null) return false;

      return valid;
    },
    totalPrice() {
      return this.priceDetail ? numberUtil.comma(this.priceDetail.paid_amount_with_wallet) : '-';
    },
    platformFee() {
      return this.priceDetail ? numberUtil.comma(this.priceDetail.platform_fee) : '-';
    },
    pricePerSession() {
      return this.priceDetail ? numberUtil.comma(this.priceDetail.price_rate) : '-';
    },
  },
  mounted() {
    this.fetchProviderOption();
  },
  methods: {
    allowedDates(date) {
      // need provider ID and duration (30, 60) for fetching available date
      if (!this.isProviderIdValid || !this.isDurationValid) {
        return false;
      }

      if (this.cachedAvailableDateSlot[date] === undefined) {
        const month = new Date(date).getMonth() + 1;
        const year = new Date(date).getFullYear();

        if (this.cachedAvailableDateSlotMonthLoaded[month] === undefined) {
          this.cachedAvailableDateSlotMonthLoaded[month] = true;

          (async () => {
            const resp = await providerService.fetchProviderScheduleMonthlyV3({
              id: `${this.providerId}`,
              durationMinute: 30,
              month: month,
              year: year,
            });
            if (resp.data.length > 0) {
              resp.data.forEach((date) => {
                const _date = date.date.replace(/T.*/, '');
                this.cachedAvailableDateSlot[_date] = date.status;
              });
            } else {
              // TODO: set all slot to false
            }

            this.$forceUpdate();
          })();
        }
      }
      return !!this.cachedAvailableDateSlot[date];
    },
    resetAvailableDateSlot() {
      this.cachedAvailableDateSlot = {};
      this.cachedAvailableDateSlotMonthLoaded = {};
      this.appointmentDate = null;
      this.appointmentTime = null;
    },
    async providerFieldChange() {
      if (this.providerId) {
        const resp = await providerService.all({ id: this.providerId });
        if (resp.data.length === 0) {
          this.providerFieldMessage = 'No provider found with this ID';
          this.isProviderIdValid = false;
          this.resetAvailableDateSlot();
        } else {
          const provider = resp.data[0];
          this.providerFieldMessage = `${provider.prefix.th} ${provider.firstname.th} ${provider.lastname.th}`;
          this.isProviderIdValid = true;
          this.resetAvailableDateSlot();
        }
      } else {
        this.providerFieldMessage = '';
        this.isProviderIdValid = false;
        this.resetAvailableDateSlot();
      }
    },
    async patientFieldChange(patientSupportPin) {
      if (patientSupportPin && patientSupportPin.length > 0) {
        const resp = await patientService.all({
          support_pin: patientSupportPin,
        });
        if (resp.data.length === 0) {
          this.patientFieldMessage = 'No patient found with this ID';
          this.isPatientProfileIdValid = false;
          this.patientProfileId = null;
          this.organizations = [];
        } else {
          const patient = resp.data[0];
          this.patientFieldMessage = `${patient.display_name} (${patient.email})`;
          this.isPatientProfileIdValid = true;
          this.patientProfileId = patient.id;
          this.organizations =
            patient.employee.length > 0
              ? patient.employee.map((emp) => {
                  return {
                    id: emp.organization.id,
                    name: emp.organization.id === 9999 ? 'B2C' : emp.organization.name,
                  };
                })
              : [patient.organization];
        }
      } else {
        this.patientFieldMessage = '';
        this.isPatientProfileIdValid = false;
        this.patientProfileId = null;
      }
    },
    async appointmentDateChange(date) {
      const resp = await providerService.fetchProviderScheduleDailyV3({
        id: `${this.providerId}`,
        durationMinute: 30,
        date: date,
      });

      const slots = resp.data;
      const freeSlots = slots.filter((e) => e.total_appointment === 0);
      this.appointmentTimes = freeSlots.map((e) => {
        const date = new Date(`${e.booking_datetime}Z`); // add `Z` so that `booking_datetime` is treated as UTC
        const fromTime = moment(date).format('HH:mm');
        const toTime = moment(date).add(this.duration, 'minute').format('HH:mm');
        return {
          text: `${fromTime} - ${toTime}`,
          value: moment(date).format('HH:mm:ss'),
        };
      });
    },
    clearInput() {
      this.providerId = null;
      this.patientSupportPin = null;
      this.duration = null;
      this.appointmentDate = null;
    },
    async create() {
      try {
        await appointmentService.create(
          this.providerId,
          this.patientProfileId,
          this.utcAppointmentDateTime,
          this.duration,
          this.priceDetail.token,
          this.selectedOrganizationId ? this.selectedOrganizationId : null
        );

        this.isShowSnackbar = true;
        this.snackbarText = 'Appointment is successfully created';
      } catch (e) {
        this.isShowSnackbar = true;
        this.snackbarText = `Failed with: ${e}`;
      }
    },
    async checkPrice() {
      if (!this.isValid) {
        // skip price calculation as required information is not valid
        this.priceDetail = null;
        return;
      }

      const resp = await appointmentService.checkout(
        this.providerId,
        this.patientProfileId,
        this.utcAppointmentDateTime,
        this.duration,
        this.selectedOrganizationId ? this.selectedOrganizationId : null
      );
      this.priceDetail = resp.data;
    },
    async fetchProviderOption(orgId) {
      try {
        const providers = await provider.all({
          archived: false,
          per_page: 500,
          order: 'id',
          sort: 'asc',
          organization_id: orgId ? orgId : 9999, // default to B2C
        });
        const options = providers.data.map((p) => ({
          text: `${p.id}: ${p.prefix.th} ${p.firstname.th} ${p.lastname.th}`,
          value: p.id,
        }));
        this.providerOptions = options;
      } catch (error) {
        console.error('Error fetching provider options:', error);
      }
    },
  },
};
